/**
 * OKLab mixing — the native half of the web's `color-mix(in oklab, …)`.
 *
 * WHY THIS EXISTS. The bidder design system builds its atmosphere layer out of
 * `color-mix(in oklab, var(--brand-primary) 5%, var(--surface))` and friends:
 * brand washes, the brand hairline, the hero band, and the dark-mode CTA fill
 * that lifts a tenant's brand toward white until it clears a near-black canvas.
 * React Native cannot read CSS custom properties and has no `color-mix`, so
 * those values have to be COMPUTED to reach a phone.
 *
 * They must be computed the SAME way the browser does them, or a tenant's app
 * and their website would show two different brand tints of the same colour —
 * the mechanical divergence rule, applied to pixels instead of arithmetic.
 *
 * OKLab rather than sRGB matters here. Mixing in sRGB darkens and desaturates
 * through the middle of a blend (the classic muddy midpoint); OKLab is
 * perceptually uniform, so a 50% mix looks like the halfway colour. A 5% brand
 * wash mixed in the wrong space reads as dirty grey rather than a tint of the
 * brand — precisely the effect the atmosphere layer exists to avoid.
 *
 * Reference: Björn Ottosson's OKLab, and CSS Color 4 §color-mix, whose
 * premultiplied-free behaviour for fully opaque colours is what is mirrored
 * here. Only opaque colours are supported; every token that uses color-mix in
 * the theme files mixes two opaque values.
 */

import { clamp, hexToRgb, rgbToHex } from './convert.js';
import type { RGB } from './types.js';

/** sRGB 0–255 → linear-light 0–1. The gamma curve, not a naive divide. */
function srgbToLinear(channel: number): number {
  const c = channel / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/** linear-light 0–1 → sRGB 0–255. */
function linearToSrgb(channel: number): number {
  const c = clamp(channel, 0, 1);
  const v = c <= 0.0031308 ? c * 12.92 : 1.055 * c ** (1 / 2.4) - 0.055;
  return v * 255;
}

interface Oklab {
  L: number;
  a: number;
  b: number;
}

export function rgbToOklab({ r, g, b }: RGB): Oklab {
  const lr = srgbToLinear(r);
  const lg = srgbToLinear(g);
  const lb = srgbToLinear(b);

  // Linear sRGB → LMS cone response, then the cube root that makes the space
  // perceptually uniform.
  const l = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb);
  const m = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb);
  const s = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb);

  return {
    L: 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    a: 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    b: 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  };
}

export function oklabToRgb({ L, a, b }: Oklab): RGB {
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;

  return {
    r: linearToSrgb(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s),
    g: linearToSrgb(-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s),
    b: linearToSrgb(-0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s),
  };
}

/**
 * `color-mix(in oklab, from <amount>%, to)` as a hex string.
 *
 * ARGUMENT ORDER MIRRORS THE CSS, deliberately. In the theme files the token
 * reads `color-mix(in oklab, var(--brand-primary) 5%, var(--surface))` — the
 * percentage belongs to the FIRST colour, and the second fills the remainder.
 * Writing this function the other way round would make every call site a
 * silent inversion of the design, so `mixOklab('#0a0045', 5, '#ffffff')` is the
 * 5%-brand wash, matching the CSS word for word.
 *
 * @param from   the colour the percentage applies to (hex)
 * @param amount 0–100, the share of `from`
 * @param to     the colour making up the remainder (hex)
 */
export function mixOklab(from: string, amount: number, to: string): string {
  const t = clamp(amount, 0, 100) / 100;
  const a = rgbToOklab(hexToRgb(from));
  const b = rgbToOklab(hexToRgb(to));
  return rgbToHex(
    oklabToRgb({
      L: b.L + (a.L - b.L) * t,
      a: b.a + (a.a - b.a) * t,
      b: b.b + (a.b - b.b) * t,
    }),
  );
}
